'use client'

import type { ReactNode } from 'react'
import { AlertTriangle, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/lib/i18n'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * The three states every vCRO block designs, so no card is ever blank: on its
 * way (a skeleton the shape of the block), failed (what failed, and a retry
 * that refetches only this block), and nothing to show (why, in words).
 */

export function CardLoading({ className, testId }: { className?: string; testId?: string }) {
  return <Skeleton className={cn('h-[220px] w-full', className)} data-testid={testId} aria-busy="true" />
}

export function CardError({ onRetry, className, testId }: { onRetry: () => void; className?: string; testId?: string }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn('flex min-h-[160px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/30 p-4 text-center', className)}
      data-testid={testId}
      role="alert"
    >
      <AlertTriangle className="size-5 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{t('vcro.riskometer.blockFailed')}</p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        {t('vcro.common.retry')}
      </Button>
    </div>
  )
}

export function CardEmpty({
  icon: Icon,
  title,
  body,
  className,
  testId,
  children,
}: {
  icon?: LucideIcon
  title: ReactNode
  body?: ReactNode
  className?: string
  testId?: string
  children?: ReactNode
}) {
  return (
    <div
      className={cn('flex min-h-[160px] flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed bg-muted/30 p-4 text-center', className)}
      data-testid={testId}
    >
      {Icon ? (
        <span className="mb-1 rounded-full bg-muted p-2.5">
          <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
        </span>
      ) : null}
      <p className="text-sm font-medium">{title}</p>
      {body ? <p className="max-w-sm text-xs text-muted-foreground">{body}</p> : null}
      {children}
    </div>
  )
}
